import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
  ICompanyInitialState,
  IEditCompanyPayload,
  IInternationalFlightTogglePayload,
  IInternationalFlightUploadPayload,
  ILoadCompanyAvatarPayload,
  ILoadCompanyCertificatesPayload,
  ISetCompanyResultPayload,
  ISetEditCompanyResultPayload,
  ISetInternationalFlightToggleResultPayload,
  ISetInternationalFlightUploadResultPayload,
  ISetLoadCompanyAvatarResultPayload,
  ISetLoadCompanyCertificatesResultPayload
} from "./companyReducerTypes";
import {
  initCommonAction, initCommonActionResult, initCommonClearAction,
  initCommonClearEditOne,
  initCommonClearOne,
  initCommonEditOne,
  initCommonGetOne, initCommonSetEditOneResult,
  initCommonSetOneResult
} from "../highOrderReducers/commonReducer";
import {mapCompanyEntityToICompany} from "./companyMappers";
import {generateEmptyCompany} from "./companyReducerEmptyData";
import {nameof} from "../../../utils/helpers/tsHelpers/nameof";


const companyInitialState: ICompanyInitialState = {
  oneItemObject: {
    item: generateEmptyCompany(),
    isLoading: true,
  },
  edit: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  loadCertificates: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  loadCompanyAvatar: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  internationalFlightToggle: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  internationalFlightUpload: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  }
}

const companySlice = createSlice({
  name: 'company',
  initialState: companyInitialState,
  reducers: {
    getCompany(state) {
      initCommonGetOne(state);
    },
    clearCompany(state) {
      initCommonClearOne(state, generateEmptyCompany);
    },
    setCompanyResult(state, {payload}: PayloadAction<ISetCompanyResultPayload>) {
      const mappedCompany = mapCompanyEntityToICompany(payload.item, payload.userEntity);
      state.oneItemObject.item = mappedCompany;
      state.oneItemObject.isLoading = false;
    },

    editCompany(state, {payload}: PayloadAction<IEditCompanyPayload>) {
      initCommonEditOne(state);
    },
    clearEditCompany(state) {
      initCommonClearEditOne(state);
    },
    setEditCompanyResult(state, {payload}: PayloadAction<ISetEditCompanyResultPayload>) {
      initCommonSetEditOneResult(state, payload, mapCompanyEntityToICompany);
    },

    loadCertificates(state, {payload}: PayloadAction<ILoadCompanyCertificatesPayload>) {
      initCommonAction(state, nameof<ICompanyInitialState>("loadCertificates"));
    },
    clearLoadCertificates(state) {
      initCommonClearAction(state, nameof<ICompanyInitialState>("loadCertificates"));
    },
    setLoadCertificatesResult(state, {payload}: PayloadAction<ISetLoadCompanyCertificatesResultPayload>) {
      if (payload.company) {
        // Map company, but preserve international flight documents if they were just uploaded
        const mappedCompany = mapCompanyEntityToICompany(payload.company);
        // If international flight documents were uploaded, update them from the response
        if (payload.userInternationalFlight) {
          const userInternationalFlight = payload.userInternationalFlight;
          
          mappedCompany.internationalFlightsEnabled = true;
          
          const caaFile = userInternationalFlight.getCaaoperatorapprovalfile();
          if (caaFile?.getFileid()) {
            mappedCompany.caaOperatorApproval = {
              uid: caaFile.getFileid(),
              name: caaFile.getName(),
              fileName: caaFile.getName(),
              url: caaFile.getFilepath(),
            };
          }
          
          const easaFile = userInternationalFlight.getEasatcoauthorizationfile();
          if (easaFile?.getFileid()) {
            mappedCompany.easaTcoAuthorisation = {
              uid: easaFile.getFileid(),
              name: easaFile.getName(),
              fileName: easaFile.getName(),
              url: easaFile.getFilepath(),
            };
          }
          
          const faaFile = userInternationalFlight.getFaaauthorizationfile();
          if (faaFile?.getFileid()) {
            mappedCompany.faaAuthorization = {
              uid: faaFile.getFileid(),
              name: faaFile.getName(),
              fileName: faaFile.getName(),
              url: faaFile.getFilepath(),
            };
          }
        }
        state.oneItemObject.item = mappedCompany;
      }
      initCommonActionResult(state, payload, nameof<ICompanyInitialState>("loadCertificates"));
    },

    loadCompanyAvatar(state, {payload}: PayloadAction<ILoadCompanyAvatarPayload>) {
      initCommonAction(state, nameof<ICompanyInitialState>("loadCompanyAvatar"));
    },
    clearLoadCompanyAvatar(state) {
      initCommonClearAction(state, nameof<ICompanyInitialState>("loadCompanyAvatar"));
    },
    setLoadCompanyAvatarResult(state, {payload}: PayloadAction<ISetLoadCompanyAvatarResultPayload>) {
      if (payload.company)
        state.oneItemObject.item = mapCompanyEntityToICompany(payload.company);
      initCommonActionResult(state, payload, nameof<ICompanyInitialState>("loadCompanyAvatar"));
    },

    internationalFlightToggle(state, {payload}: PayloadAction<IInternationalFlightTogglePayload>) {
      initCommonAction(state, nameof<ICompanyInitialState>("internationalFlightToggle"));
    },
    clearInternationalFlightToggle(state) {
      initCommonClearAction(state, nameof<ICompanyInitialState>("internationalFlightToggle"));
    },
    setInternationalFlightToggleResult(state, {payload}: PayloadAction<ISetInternationalFlightToggleResultPayload>) {
      initCommonActionResult(state, payload, nameof<ICompanyInitialState>("internationalFlightToggle"));
      // Update company.internationalFlightsEnabled and documents if toggle was successful
      if (payload.success && payload.enabled !== undefined) {
        state.oneItemObject.item.internationalFlightsEnabled = payload.enabled;
        
        // Update documents if UserInternationalFlightEntity is provided
        if (payload.userInternationalFlight) {
          const userInternationalFlight = payload.userInternationalFlight;
          
          const caaFile = userInternationalFlight.getCaaoperatorapprovalfile();
          if (caaFile?.getFileid()) {
            state.oneItemObject.item.caaOperatorApproval = {
              uid: caaFile.getFileid(),
              name: caaFile.getName(),
              fileName: caaFile.getName(),
              url: caaFile.getFilepath(),
            };
          }
          
          const easaFile = userInternationalFlight.getEasatcoauthorizationfile();
          if (easaFile?.getFileid()) {
            state.oneItemObject.item.easaTcoAuthorisation = {
              uid: easaFile.getFileid(),
              name: easaFile.getName(),
              fileName: easaFile.getName(),
              url: easaFile.getFilepath(),
            };
          }
          
          const faaFile = userInternationalFlight.getFaaauthorizationfile();
          if (faaFile?.getFileid()) {
            state.oneItemObject.item.faaAuthorization = {
              uid: faaFile.getFileid(),
              name: faaFile.getName(),
              fileName: faaFile.getName(),
              url: faaFile.getFilepath(),
            };
          }
        } else if (!payload.enabled) {
          // If disabled, clear the documents
          state.oneItemObject.item.caaOperatorApproval = undefined;
          state.oneItemObject.item.easaTcoAuthorisation = undefined;
          state.oneItemObject.item.faaAuthorization = undefined;
        }
      }
    },

    internationalFlightUpload(state, {payload}: PayloadAction<IInternationalFlightUploadPayload>) {
      initCommonAction(state, nameof<ICompanyInitialState>("internationalFlightUpload"));
    },
    clearInternationalFlightUpload(state) {
      initCommonClearAction(state, nameof<ICompanyInitialState>("internationalFlightUpload"));
    },
    setInternationalFlightUploadResult(state, {payload}: PayloadAction<ISetInternationalFlightUploadResultPayload>) {
      initCommonActionResult(state, payload, nameof<ICompanyInitialState>("internationalFlightUpload"));
      // Update company with uploaded documents if upload was successful
      if (payload.success && payload.userInternationalFlight) {
        const userInternationalFlight = payload.userInternationalFlight;
        
        // Map uploaded files to UploadFile format
        const caaFile = userInternationalFlight.getCaaoperatorapprovalfile();
        if (caaFile?.getFileid()) {
          state.oneItemObject.item.caaOperatorApproval = {
            uid: caaFile.getFileid(),
            name: caaFile.getName(),
            fileName: caaFile.getName(),
            url: caaFile.getFilepath(),
          };
        }
        
        const easaFile = userInternationalFlight.getEasatcoauthorizationfile();
        if (easaFile?.getFileid()) {
          state.oneItemObject.item.easaTcoAuthorisation = {
            uid: easaFile.getFileid(),
            name: easaFile.getName(),
            fileName: easaFile.getName(),
            url: easaFile.getFilepath(),
          };
        }
        
        const faaFile = userInternationalFlight.getFaaauthorizationfile();
        if (faaFile?.getFileid()) {
          state.oneItemObject.item.faaAuthorization = {
            uid: faaFile.getFileid(),
            name: faaFile.getName(),
            fileName: faaFile.getName(),
            url: faaFile.getFilepath(),
          };
        }
      }
    },
  },
});

export const {
  getCompany,
  clearCompany,
  setCompanyResult,

  editCompany,
  clearEditCompany,
  setEditCompanyResult,

  loadCertificates,
  clearLoadCertificates,
  setLoadCertificatesResult,

  loadCompanyAvatar,
  clearLoadCompanyAvatar,
  setLoadCompanyAvatarResult,

  internationalFlightToggle,
  clearInternationalFlightToggle,
  setInternationalFlightToggleResult,

  internationalFlightUpload,
  clearInternationalFlightUpload,
  setInternationalFlightUploadResult,

} = companySlice.actions;
export default companySlice.reducer;
